/**
 * Panel A — the Briefs sub-tab table (§5.7.2).
 *
 * Columns: Keyword · Source brief status · Off-Page Brief status · Action.
 *
 * Behavior:
 *  - `can_generate: true`   → primary "Generate Off-Page Brief" button.
 *  - `can_generate: false`  → disabled button; hover tooltip carries the
 *                             API-supplied `disabled_reason`.
 *  - Row already has an `off_page_brief_id` → "Open" link to the workspace.
 *
 * The mutation error surface handles two named codes explicitly:
 *  - `SOURCE_BRIEF_NOT_COMPLETED` → inline banner under the affected row.
 *  - `OFF_PAGE_BRIEF_ALREADY_EXISTS` → swap the button for "Already generated
 *    — open it" and re-fetch so the eligible list picks up the new state.
 * Any other error falls through to the shared humanised copy.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertCircle, Notebook, Sparkles } from 'lucide-react';
import type { EligibleSourceBrief } from '@seo/types';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/status-pill';
import { Spinner } from '@/components/spinner';
import { ErrorBanner } from '@/components/error-banner';
import { EmptyState } from '@/components/ui/empty';
import { DataTable } from '@/components/data-table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ApiError } from '@/lib/api';
import { useEligibleSourceBriefs } from '@/features/off-page-briefs/use-eligible-source-briefs';
import { useCreateOffPageBrief } from '@/features/off-page-briefs/use-create-off-page-brief';

export interface BriefsTableProps {
  /**
   * The project the tenant has selected. When undefined we render an
   * empty-state prompting them to pick one — the query is disabled.
   */
  projectId: string | undefined;
  /** Toolbar-controlled substring filter over keyword + status text. */
  query?: string;
}

export function BriefsTable({ projectId, query = '' }: BriefsTableProps): JSX.Element {
  const eligible = useEligibleSourceBriefs(projectId);
  const createOpb = useCreateOffPageBrief();
  // Which row is the mutation currently targeting? Used to (a) show a spinner
  // on the button being clicked and (b) render an inline error under just
  // that row.
  const [pendingBriefId, setPendingBriefId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{
    briefId: string;
    error: unknown;
  } | null>(null);

  const rows = useMemo(() => {
    const items = eligible.data?.items ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (r) =>
        r.target_keyword?.toLowerCase().includes(q) ||
        r.brief_status.toLowerCase().includes(q) ||
        (r.off_page_brief_status?.toLowerCase().includes(q) ?? false),
    );
  }, [eligible.data, query]);

  if (!projectId) {
    return (
      <EmptyState
        icon={Notebook}
        title="Pick a project"
        description="Off-Page Briefs are scoped to a single project — pick one above to see eligible source briefs."
      />
    );
  }

  if (eligible.error) {
    return <ErrorBanner error={eligible.error} />;
  }

  return (
    <DataTable<EligibleSourceBrief>
      loading={eligible.isLoading}
      rows={rows}
      rowKey={(r) => r.brief_id}
      columns={[
        {
          key: 'keyword',
          header: 'Keyword',
          cell: (r) => (
            <span className="truncate text-sm font-medium">
              {r.target_keyword.trim() || `Brief ${r.brief_id.slice(0, 8)}`}
            </span>
          ),
        },
        {
          key: 'source-status',
          header: 'Source brief status',
          cell: (r) => <StatusPill status={r.brief_status} />,
          className: 'w-40',
        },
        {
          key: 'opb-status',
          header: 'Off-Page Brief status',
          cell: (r) =>
            r.off_page_brief_status ? (
              <StatusPill status={r.off_page_brief_status} />
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            ),
          className: 'w-40',
        },
        {
          key: 'action',
          header: 'Action',
          align: 'right',
          className: 'w-64',
          cell: (r) => (
            <RowAction
              row={r}
              pending={pendingBriefId === r.brief_id && createOpb.isPending}
              rowError={
                rowError?.briefId === r.brief_id ? rowError.error : undefined
              }
              onGenerate={() => {
                setRowError(null);
                setPendingBriefId(r.brief_id);
                createOpb.mutate(
                  { briefId: r.brief_id },
                  {
                    onSettled: () => setPendingBriefId(null),
                    onError: (err) => setRowError({ briefId: r.brief_id, error: err }),
                    onSuccess: () => setRowError(null),
                  },
                );
              }}
            />
          ),
        },
      ]}
      emptyState={
        <EmptyState
          icon={Notebook}
          title="No briefs to distribute yet"
          description="An Off-Page Brief piggybacks on a completed Content Brief for the same keyword. Write one in Briefs first (takes ~90 seconds), then come back here and click Generate."
          action={
            <Button asChild>
              <Link to="/briefs">Write your first brief →</Link>
            </Button>
          }
        />
      }
    />
  );
}

interface RowActionProps {
  row: EligibleSourceBrief;
  pending: boolean;
  rowError: unknown;
  onGenerate: () => void;
}

function RowAction({ row, pending, rowError, onGenerate }: RowActionProps): JSX.Element {
  // Case 1: row already has an off-page brief — render an Open link.
  if (row.off_page_brief_id) {
    return (
      <div className="flex justify-end">
        <Button asChild size="sm" variant="outline">
          <Link
            to="/off-page-briefs/$offPageBriefId"
            params={{ offPageBriefId: row.off_page_brief_id }}
          >
            Open
          </Link>
        </Button>
      </div>
    );
  }

  // Case 2: the mutation returned OFF_PAGE_BRIEF_ALREADY_EXISTS — swap to Open.
  // The refetch fired by the mutation's onSuccess will replace this row with
  // one that has an `off_page_brief_id`, but until it lands we already know
  // the OPB exists and shouldn't leave the button in a broken state.
  if (rowError instanceof ApiError && rowError.code === 'OFF_PAGE_BRIEF_ALREADY_EXISTS') {
    return (
      <div className="flex flex-col items-end gap-1">
        <span className="text-xs text-muted-foreground">
          Already generated — refreshing…
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Spinner /> Loading list
        </span>
      </div>
    );
  }

  // Case 3: not eligible — disabled button with a tooltip carrying the reason.
  if (!row.can_generate) {
    return (
      <div className="flex justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            {/* The wrapper span is required so a disabled button still emits
                pointer events for the tooltip trigger. */}
            <span className="inline-block">
              <Button size="sm" variant="outline" disabled>
                Generate Off-Page Brief
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            {row.disabled_reason ?? 'Not eligible yet'}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // Case 4: eligible — normal generate button + inline error for named codes.
  return (
    <div className="flex flex-col items-end gap-1">
      <Button size="sm" onClick={onGenerate} disabled={pending}>
        {pending ? (
          <>
            <Spinner /> Generating…
          </>
        ) : (
          <>
            <Sparkles className="h-3.5 w-3.5" /> Generate Off-Page Brief
          </>
        )}
      </Button>
      {rowError instanceof ApiError && rowError.code === 'SOURCE_BRIEF_NOT_COMPLETED' ? (
        <span className="inline-flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3 w-3" />
          Source brief isn't complete yet.
        </span>
      ) : rowError ? (
        <span className="inline-flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3 w-3" />
          Could not generate — try again.
        </span>
      ) : null}
    </div>
  );
}
